import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, updateDoc, query, where, orderBy } from "firebase/firestore";
import { firestore } from "../lib/firebaseConfig";
import PageWrapper from '../components/PageWrapper';
import PageTitle from '../components/PageTitle';
import Card from '../components/Card';
import Button from '../components/Button';
import Spinner from '../components/Spinner';
import { cn } from '../lib/utils';
import { generateXLSX } from '../lib/xlsxGenerator';
import { sendEmail } from '../lib/emailService';
import { paymentConfirmationTemplate } from '../lib/emailTemplates';
import { useToast } from '../context/ToastContext';
import Pagination from '../components/Pagination';

// Pieciostopniowy model statusu faktury. Klucze angielskie, bo takie byly w bazie
// od poczatku; etykiety polskie, bo to one trafiaja do ksiegowej. `status` jest
// JEDYNYM zrodlem prawdy o tym, gdzie faktura jest w obiegu - `asseco_exported`
// zostaje wylacznie jako pole audytowe z historia realnych eksportow.
const INVOICE_STATUSES = {
  pending: { label: 'Oczekująca', color: 'bg-[#E99B67]' },
  issued: { label: 'Wystawiona', color: 'bg-[#6BA8D6]' },
  sent_to_client: { label: 'Wysłana do klienta', color: 'bg-[#5CC184]' },
  sent_to_asseco: { label: 'Wysłana do Asseco', color: 'bg-[#9B8BD6]' },
  imported_in_asseco: { label: 'Zaimportowana w Asseco', color: 'bg-[#4A7C59]' },
  cancelled: { label: 'Anulowana', color: 'bg-[#BEBEBE]' }
};

// Faktura jeszcze nieobecna w Asseco, czyli taka, ktora ma sens eksportowac.
const doEksportu = (inv) =>
  inv.status !== 'imported_in_asseco' && inv.status !== 'cancelled';

const Invoices = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const [activeTab, setActiveTab] = useState('do_eksportu');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [sendingInvoiceId, setSendingInvoiceId] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [flatFilter, setFlatFilter] = useState('all');
  const [investmentFilter, setInvestmentFilter] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Fetch stage settlements from Firebase
      const settlementsSnapshot = await getDocs(
        query(collection(firestore, 'stage_settlements'), orderBy('date', 'desc'))
      );

      const allInvoices = settlementsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setInvoices(allInvoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      showError('Nie udało się pobrać faktur');
    }
    setLoading(false);
  };

  const updateInvoiceStatus = async (invoice, newStatus) => {
    try {
      const invoiceRef = doc(firestore, 'stage_settlements', invoice.id);

      const updateData = {
        status: newStatus,
        invoice_sent: newStatus === 'sent_to_client'
      };

      if (newStatus === 'sent_to_client') {
        updateData.invoice_sent_at = new Date().toISOString();
      }

      await updateDoc(invoiceRef, updateData);

      // Update local state
      setInvoices(prev => prev.map(inv =>
        inv.id === invoice.id
          ? { ...inv, status: newStatus, invoice_sent: updateData.invoice_sent, invoice_sent_at: updateData.invoice_sent_at }
          : inv
      ));

      return true;
    } catch (error) {
      console.error('Error updating invoice status:', error);
      showError('Nie udało się zaktualizować statusu faktury.');
      return false;
    }
  };

  const handlePreviewInvoice = (invoice) => {
    if (invoice.pdf_url) {
      window.open(invoice.pdf_url, '_blank');
    } else {
      showError('Brak pliku PDF dla tej faktury');
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    if (!invoice.pdf_url) {
      showError('Brak pliku PDF dla tej faktury');
      return;
    }
    try {
      const response = await fetch(invoice.pdf_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Faktura_${invoice.invoice_number?.replace(/\//g, '-') || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError('Błąd podczas pobierania faktury');
    }
  };

  const handleSendInvoiceEmail = async (invoice) => {
    if (!invoice.pdf_url) {
      showError('Brak pliku PDF dla tej faktury');
      return false;
    }

    const testEmail = 'pawel.reszka@inigra.eu';

    // Get PDF as base64
    let pdfBase64;
    try {
      const response = await fetch(invoice.pdf_url);
      const blob = await response.blob();
      const reader = new FileReader();
      pdfBase64 = await new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error fetching PDF:', error);
      showError('Błąd podczas pobierania faktury');
      return false;
    }

    // Generate email content
    const emailTemplate = paymentConfirmationTemplate({
      customerName: invoice.client_name || 'Szanowny Kliencie',
      apartmentCode: invoice.flat_display_code,
      paidAmount: invoice.amount_brutto,
      paymentDate: invoice.date,
      installmentNumber: `Etap ${invoice.stage_number}`,
      contractNumber: invoice.contract_number || '-',
      invoiceNumber: invoice.invoice_number,
      remainingAmount: 0
    });

    // Send email with attachment
    await sendEmail(
      testEmail,
      emailTemplate.subject,
      emailTemplate.html,
      emailTemplate.text,
      [{
        content: pdfBase64,
        filename: `Faktura_${invoice.invoice_number.replace(/\//g, '-')}.pdf`,
        type: 'application/pdf',
        disposition: 'attachment'
      }],
      {
        type: 'stage_invoice',
        settlementId: invoice.id,
        flatCode: invoice.flat_display_code,
        invoiceNumber: invoice.invoice_number
      }
    );

    return true;
  };

  // Test email override
  const TEST_EMAIL = 'pawel.reszka@inigra.eu';

  const handleSendInvoice = async (invoice) => {
    const recipientEmail = TEST_EMAIL;
    const confirmed = window.confirm(
      `Czy na pewno chcesz wysłać fakturę ${invoice.invoice_number}?\n\nLokal: ${invoice.flat_display_code}\nEtap: ${invoice.stage_number}\nKwota: ${invoice.amount_brutto?.toLocaleString('pl-PL')} zł\n\nEmail zostanie wysłany na: ${recipientEmail}`
    );

    if (!confirmed) return;

    setSendingInvoiceId(invoice.id);
    try {
      await handleSendInvoiceEmail(invoice);
      await updateInvoiceStatus(invoice, 'sent_to_client');
      showSuccess(`Faktura została wysłana na adres: ${recipientEmail}`);
    } catch (error) {
      showError(`Błąd podczas wysyłania faktury: ${error.message}`);
    } finally {
      setSendingInvoiceId(null);
    }
  };

  const handleResendInvoice = async (invoice) => {
    const recipientEmail = TEST_EMAIL;
    const confirmed = window.confirm(
      `Czy na pewno chcesz ponownie wysłać fakturę ${invoice.invoice_number}?\n\nEmail zostanie wysłany na: ${recipientEmail}`
    );

    if (!confirmed) return;

    setSendingInvoiceId(invoice.id);
    try {
      await handleSendInvoiceEmail(invoice);
      showSuccess(`Faktura została ponownie wysłana na adres: ${recipientEmail}`);
    } catch (error) {
      showError(`Błąd podczas wysyłania faktury: ${error.message}`);
    } finally {
      setSendingInvoiceId(null);
    }
  };

  const handleCancelInvoice = async (invoice) => {
    const confirmed = window.confirm(
      `Czy na pewno chcesz anulować fakturę ${invoice.invoice_number}?`
    );
    if (confirmed) {
      await updateInvoiceStatus(invoice, 'cancelled');
    }
  };

  const handleRestoreInvoice = async (invoice) => {
    await updateInvoiceStatus(invoice, 'pending');
  };

  // Selection handlers
  const toggleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const pendingInvoices = getFilteredInvoices().filter(doEksportu);
    if (selectedInvoices.size === pendingInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(pendingInvoices.map(inv => inv.id)));
    }
  };

  // Asseco importuje w kontekście JEDNEJ spółki i odrzuca plik, w którym "Symbol magazynu"
  // nie zgadza się z zalogowaną firmą. Dlatego eksport zawsze rozbijamy na osobny plik
  // per spółka, nawet gdy zaznaczono faktury z kilku.
  const pobierzPoSpolkach = (lista, prefiks) => {
    const today = new Date().toISOString().split('T')[0];
    const grupy = lista.reduce((acc, inv) => {
      const sp = String(inv.company_id || '00');
      (acc[sp] = acc[sp] || []).push(inv);
      return acc;
    }, {});
    const spolki = Object.keys(grupy).sort();
    spolki.forEach((sp) => {
      generateXLSX(grupy[sp], `${prefiks}_spolka-${sp}_${today}.xlsx`);
    });
    return spolki;
  };

  const handleDownloadXLSX = () => {
    const wybrane = invoices.filter(inv => selectedInvoices.has(inv.id));
    // Zaznaczone, ale już obecne w Asseco - nie wchodzą do pliku (patrz handleDownloadAllXLSX).
    const selectedList = wybrane.filter(inv => !inv.asseco_exported);
    const pominiete = wybrane.length - selectedList.length;

    if (selectedList.length === 0) {
      showWarning(pominiete > 0
        ? `Wszystkie zaznaczone faktury (${pominiete}) są już wystawione w Asseco`
        : 'Nie wybrano żadnych faktur do eksportu');
      return;
    }

    const spolki = pobierzPoSpolkach(selectedList, 'faktury');
    const opisPliki = spolki.length > 1
      ? `${spolki.length} plików (po jednym na spółkę: ${spolki.join(', ')})`
      : '1 plik';
    showSuccess(pominiete > 0
      ? `Pobrano ${opisPliki}, ${selectedList.length} faktur (pominięto ${pominiete} już w Asseco)`
      : `Pobrano ${opisPliki}, ${selectedList.length} faktur`);
  };

  const handleDownloadAllXLSX = () => {
    // asseco_exported = faktura już jest w Asseco (zaimportowana albo wystawiona ręcznie
    // przez księgowość). Pomijamy, żeby nie wgrać jej po raz drugi.
    const pendingList = invoices.filter(doEksportu);
    const pominiete = invoices.filter(inv => inv.status === 'imported_in_asseco').length;

    if (pendingList.length === 0) {
      showWarning('Brak oczekujących faktur do eksportu');
      return;
    }
    if (pominiete > 0) {
      showWarning(`Pominięto ${pominiete} faktur już wystawionych w Asseco`);
    }

    const spolki = pobierzPoSpolkach(pendingList, 'faktury_oczekujace');
    showSuccess(spolki.length > 1
      ? `Pobrano ${spolki.length} plików (po jednym na spółkę: ${spolki.join(', ')}), razem ${pendingList.length} faktur`
      : `Pobrano XLSX z ${pendingList.length} fakturami`);
  };

  const handleSendSelected = async () => {
    const selectedList = invoices.filter(inv => selectedInvoices.has(inv.id) && doEksportu(inv));

    if (selectedList.length === 0) {
      showWarning('Nie wybrano żadnych faktur do wysłania');
      return;
    }

    const withEmail = selectedList.filter(inv => inv.client_emails?.length);
    const withoutEmail = selectedList.filter(inv => !inv.client_emails?.length);

    let message = `Czy na pewno chcesz wysłać ${withEmail.length} faktur?`;
    if (withoutEmail.length > 0) {
      message += `\n\n${withoutEmail.length} faktur nie zostanie wysłanych (brak adresu email).`;
    }

    const confirmed = window.confirm(message);
    if (!confirmed) return;

    setSendingBulk(true);
    let sent = 0;
    let failed = 0;

    for (const invoice of withEmail) {
      try {
        await handleSendInvoiceEmail(invoice);
        await updateInvoiceStatus(invoice, 'sent_to_client');
        sent++;
      } catch (error) {
        console.error(`Failed to send invoice ${invoice.invoice_number}:`, error);
        failed++;
      }
    }

    setSendingBulk(false);
    setSelectedInvoices(new Set());

    if (failed === 0) {
      showSuccess(`Wysłano ${sent} faktur`);
    } else {
      showWarning(`Wysłano ${sent} faktur, ${failed} nie udało się wysłać`);
    }
  };

  // Sorting handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Unique investment names for filter dropdown
  const uniqueInvestments = [...new Set(invoices.map(inv => inv.investment_name))].filter(Boolean).sort();

  // Unique flat codes for filter dropdown (narrowed to the selected investment)
  const uniqueFlats = [...new Set(
    invoices
      .filter(inv => investmentFilter === 'all' || inv.investment_name === investmentFilter)
      .map(inv => inv.flat_display_code)
  )].filter(Boolean).sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, '')) || 0;
    const numB = parseInt(b.replace(/\D/g, '')) || 0;
    return numA - numB;
  });

  // Filter invoices
  const getFilteredInvoices = () => {
    let filtered = invoices;

    // Filter by tab
    if (activeTab === 'do_eksportu') {
      filtered = filtered.filter(doEksportu);
    } else if (activeTab === 'imported_in_asseco') {
      filtered = filtered.filter(inv => inv.status === 'imported_in_asseco');
    }

    // Filter by status dropdown (only for 'all' tab)
    if (activeTab === 'all' && statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Filter by investment
    if (investmentFilter !== 'all') {
      filtered = filtered.filter(inv => inv.investment_name === investmentFilter);
    }

    // Filter by flat
    if (flatFilter !== 'all') {
      filtered = filtered.filter(inv => inv.flat_display_code === flatFilter);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.flat_display_code?.toLowerCase().includes(term) ||
        inv.client_name?.toLowerCase().includes(term) ||
        inv.invoice_number?.toLowerCase().includes(term) ||
        inv.contract_number?.toLowerCase().includes(term)
      );
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      let valA, valB;

      switch (sortField) {
        case 'invoice_number':
          valA = a.invoice_number || '';
          valB = b.invoice_number || '';
          break;
        case 'flat':
          const numA = parseInt(a.flat_display_code?.replace(/\D/g, '')) || 0;
          const numB = parseInt(b.flat_display_code?.replace(/\D/g, '')) || 0;
          valA = numA;
          valB = numB;
          break;
        case 'stage':
          valA = a.stage_number || 0;
          valB = b.stage_number || 0;
          break;
        case 'client':
          valA = a.client_name || '';
          valB = b.client_name || '';
          break;
        case 'amount':
          valA = a.amount_brutto || 0;
          valB = b.amount_brutto || 0;
          break;
        case 'date':
          valA = a.date || '';
          valB = b.date || '';
          break;
        default:
          valA = a[sortField] || '';
          valB = b[sortField] || '';
      }

      if (typeof valA === 'string') {
        const cmp = valA.localeCompare(valB, 'pl');
        return sortDirection === 'asc' ? cmp : -cmp;
      } else {
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }
    });

    return filtered;
  };

  const filteredInvoices = getFilteredInvoices();

  // Paginate
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    setSelectedInvoices(new Set());
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleFlatFilterChange = (e) => {
    setFlatFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleInvestmentFilterChange = (e) => {
    setInvestmentFilter(e.target.value);
    setFlatFilter('all'); // reset flat when investment changes (flat list is scoped to investment)
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setFlatFilter('all');
    setInvestmentFilter('all');
    setSortField('date');
    setSortDirection('desc');
    setCurrentPage(1);
  };

  const basicTdClasses = 'py-[12px] px-[12px] text-left';

  // Sort indicator component
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const SortableHeader = ({ field, children }) => (
    <th
      className={`${basicTdClasses} font-medium cursor-pointer hover:bg-gray-100 select-none`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center">
        {children}
        <SortIndicator field={field} />
      </div>
    </th>
  );

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Trzy zakladki zamiast piatki, zeby pasek nie puchl. Precyzyjne filtrowanie
  // po wszystkich statusach jest w liscie rozwijalnej na zakladce "Wszystkie".
  const tabs = [
    { id: 'do_eksportu', label: 'Do eksportu', count: invoices.filter(doEksportu).length },
    { id: 'imported_in_asseco', label: 'W Asseco', count: invoices.filter(i => i.status === 'imported_in_asseco').length },
    { id: 'all', label: 'Wszystkie', count: invoices.length }
  ];

  const pendingInvoices = getFilteredInvoices().filter(inv => inv.status === 'pending');
  const allSelected = pendingInvoices.length > 0 && selectedInvoices.size === pendingInvoices.length;

  return (
    <PageWrapper>
      <PageTitle>Faktury - Rozliczenia Etapów</PageTitle>

      <Card className="mt-[20px]">
        {/* Tabs */}
        <div className="flex border-b border-[var(--bright-gray)]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                'px-6 py-4 font-medium text-sm transition-colors relative',
                activeTab === tab.id
                  ? 'text-[var(--electric-blue)] border-b-2 border-[var(--electric-blue)]'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              {tab.label}
              <span className={cn(
                'ml-2 px-2 py-0.5 rounded-full text-xs',
                activeTab === tab.id ? 'bg-[var(--electric-blue)] text-white' : 'bg-gray-200'
              )}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Filters & Actions */}
        <div className="p-4 border-b border-[var(--bright-gray)] flex gap-4 items-center flex-wrap">
          <input
            type="text"
            placeholder="Szukaj (lokal, klient, faktura)..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="px-4 py-2 border border-[#D7D7D7] rounded-[6px] w-[300px] focus:outline-none focus:border-[var(--electric-blue)]"
          />

          <select
            value={investmentFilter}
            onChange={handleInvestmentFilterChange}
            className="px-4 py-2 border border-[#D7D7D7] rounded-[6px] focus:outline-none focus:border-[var(--electric-blue)]"
          >
            <option value="all">Wszystkie inwestycje</option>
            {uniqueInvestments.map(inv => (
              <option key={inv} value={inv}>{inv}</option>
            ))}
          </select>

          <select
            value={flatFilter}
            onChange={handleFlatFilterChange}
            className="px-4 py-2 border border-[#D7D7D7] rounded-[6px] focus:outline-none focus:border-[var(--electric-blue)]"
          >
            <option value="all">Wszystkie lokale</option>
            {uniqueFlats.map(flat => (
              <option key={flat} value={flat}>{flat}</option>
            ))}
          </select>

          {activeTab === 'all' && (
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="px-4 py-2 border border-[#D7D7D7] rounded-[6px] focus:outline-none focus:border-[var(--electric-blue)]"
            >
              <option value="all">Wszystkie statusy</option>
              {Object.entries(INVOICE_STATUSES).map(([key, s]) => (
                <option key={key} value={key}>{s.label}</option>
              ))}
            </select>
          )}

          {(searchTerm || flatFilter !== 'all' || investmentFilter !== 'all' || statusFilter !== 'all') && (
            <button
              onClick={handleResetFilters}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Wyczyść filtry
            </button>
          )}

          <div className="ml-auto flex gap-2">
            {activeTab === 'pending' && (
              <>
                {selectedInvoices.size > 0 && (
                  <>
                    <Button
                      variant="bordered"
                      onClick={handleDownloadXLSX}
                    >
                      Pobierz XLSX ({selectedInvoices.size})
                    </Button>
                    <Button
                      variant="default"
                      onClick={handleSendSelected}
                      disabled={sendingBulk}
                    >
                      {sendingBulk ? 'Wysyłanie...' : `Wyślij zaznaczone (${selectedInvoices.size})`}
                    </Button>
                  </>
                )}
                <Button variant="bordered" onClick={handleDownloadAllXLSX}>
                  Eksportuj wszystkie XLSX
                </Button>
              </>
            )}
            <Button variant="bordered" onClick={fetchInvoices}>
              Odśwież
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-12 flex flex-col items-center justify-center gap-4">
              <Spinner size="lg" />
              <span className="text-gray-500">Ładowanie faktur...</span>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {activeTab === 'pending' && 'Brak oczekujących faktur'}
              {activeTab === 'sent' && 'Brak wysłanych faktur'}
              {activeTab === 'all' && 'Brak faktur'}
            </div>
          ) : (
            <table className="w-full whitespace-nowrap">
              <thead>
                <tr className="bg-[var(--lotion)] border-b border-[var(--bright-gray)]">
                  {activeTab === 'pending' && (
                    <th className={`${basicTdClasses} font-medium w-[40px]`}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        className="w-4 h-4"
                      />
                    </th>
                  )}
                  <SortableHeader field="invoice_number">Nr faktury</SortableHeader>
                  <SortableHeader field="flat">Lokal</SortableHeader>
                  <SortableHeader field="stage">Etap</SortableHeader>
                  <SortableHeader field="client">Nabywca</SortableHeader>
                  <SortableHeader field="amount">Kwota brutto</SortableHeader>
                  <th className={`${basicTdClasses} font-medium`}>VAT</th>
                  <SortableHeader field="date">Data</SortableHeader>
                  <th className={`${basicTdClasses} font-medium`}>Status</th>
                  <th className={`${basicTdClasses} font-medium`}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-[var(--bright-gray)] hover:bg-gray-50">
                    {activeTab === 'pending' && (
                      <td className={basicTdClasses}>
                        <input
                          type="checkbox"
                          checked={selectedInvoices.has(invoice.id)}
                          onChange={() => toggleSelectInvoice(invoice.id)}
                          className="w-4 h-4"
                        />
                      </td>
                    )}
                    <td className={`${basicTdClasses} font-medium`}>
                      {invoice.invoice_number}
                      {invoice.asseco_exported && (
                        <div
                          className="text-[10px] font-normal text-[#6BA8D6] mt-0.5"
                          title={invoice.asseco_export_note || 'Faktura już jest w Asseco - nie eksportować ponownie'}
                        >
                          w Asseco
                        </div>
                      )}
                    </td>
                    <td className={basicTdClasses}>{invoice.flat_display_code}</td>
                    <td className={basicTdClasses}>Etap {invoice.stage_number}</td>
                    <td className={basicTdClasses}>
                      <div>{invoice.client_name}</div>
                      {/* Przy malzenstwie kazdy nabywca ma swoj adres i faktura
                          idzie na oba, dlatego pokazujemy wszystkie. */}
                      {invoice.client_emails?.map(mail => (
                        <div key={mail} className="text-xs text-gray-500">{mail}</div>
                      ))}
                    </td>
                    <td className={`${basicTdClasses} font-medium`}>
                      {invoice.amount_brutto?.toLocaleString('pl-PL')} zł
                    </td>
                    <td className={basicTdClasses}>{Math.round(invoice.vat_rate * 100)}%</td>
                    <td className={basicTdClasses}>{invoice.date}</td>
                    <td className={basicTdClasses}>
                      <span className={cn(
                        'text-[11px] px-2 py-1 rounded-[4px] text-white',
                        INVOICE_STATUSES[invoice.status]?.color || 'bg-gray-400'
                      )}>
                        {INVOICE_STATUSES[invoice.status]?.label || invoice.status}
                      </span>
                    </td>
                    <td className={basicTdClasses}>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          variant="bordered"
                          className="text-xs py-1 px-3"
                          onClick={() => handlePreviewInvoice(invoice)}
                        >
                          Podgląd
                        </Button>
                        <Button
                          variant="bordered"
                          className="text-xs py-1 px-3"
                          onClick={() => handleDownloadInvoice(invoice)}
                        >
                          Pobierz
                        </Button>

                        {invoice.status === 'pending' && (
                          <>
                            <Button
                              variant="default"
                              className="text-xs py-1 px-3"
                              onClick={() => handleSendInvoice(invoice)}
                              disabled={sendingInvoiceId === invoice.id || !invoice.client_emails?.length}
                              title={!invoice.client_emails?.length ? 'Brak adresu email' : ''}
                            >
                              {sendingInvoiceId === invoice.id ? 'Wysyłanie...' : 'Wyślij'}
                            </Button>
                            <Button
                              variant="bordered"
                              className="text-xs py-1 px-3"
                              onClick={() => handleCancelInvoice(invoice)}
                            >
                              Anuluj
                            </Button>
                          </>
                        )}
                        {invoice.status === 'sent_to_client' && (
                          <Button
                            variant="bordered"
                            className="text-xs py-1 px-3"
                            onClick={() => handleResendInvoice(invoice)}
                            disabled={sendingInvoiceId === invoice.id}
                          >
                            {sendingInvoiceId === invoice.id ? 'Wysyłanie...' : 'Wyślij ponownie'}
                          </Button>
                        )}
                        {invoice.status === 'cancelled' && (
                          <Button
                            variant="bordered"
                            className="text-xs py-1 px-3"
                            onClick={() => handleRestoreInvoice(invoice)}
                          >
                            Przywróć
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredInvoices.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={filteredInvoices.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </Card>
    </PageWrapper>
  );
};

export default Invoices;
